/**
 * puddle_sim — THE PUDDLE SYSTEM's pure half (shader gaps program R6, 2026-09-30, the owner's "shallow-water simulation
 * … for forming puddles when it rains" + "the combination of static puddles and shallow water simulation").
 *
 * ONE PUDDLE SYSTEM, TWO LAYERS:
 *   • THE STATIC LAYER — `lib/wetness.ts`'s procedural hollows (two noise scales, the rules' wet mask, the ruts) filled by
 *     THE ONE WET CLOCK's pond (agent E, `wet_state.ts`) — every tier, every distance, WebGL2, VR, the far ground. Kept.
 *   • THE SIMULATED LAYER — a camera-following virtual-pipes patch (Mei, Decaudin & Hu 2007; engine `ShallowWaterGpu`, its
 *     CPU oracle `ShallowWaterSolver` in patch mode) on the DRAWN ground (`meshHeight` — the resident chunk's own
 *     triangles), with the static hollows subtracted from its bed as MICRO-RELIEF (the same noise, the same two scales,
 *     `reliefDepthM`): rain × the roof map's exposure runs downhill, fills the terrain's real lows AND the hollows the 0.5 m
 *     cells cannot draw, soaks in by ground class, evaporates at the clock's own rate, drains into still water. Inside
 *     the patch the ground's puddle term reads ONLY the sim (no double counting); over the last `HANDOVER_M` it blends to
 *     the static fill, CALIBRATED against the sim (`verify:puddles` pins the band) so walking out changes nothing visible.
 *
 * THE RATES ARE THE CLOCK'S. Rain in = the clock's rain rate (mm per GAME hour) × its time scale × the cell's exposure;
 * evaporation = Carrier's rate for the soil class (`evaporationMmH`, the clock's inputs); infiltration = the soil pond's
 * drain (`WET_PARAMS.soil.infilMmH / 3` × the pore room — the SAME rule that empties the static pond) × the ground
 * class (sand soaks fast, rock and roads not at all). The patch READS the clock (`wetClock.lastInputs`, `.state.soil`)
 * and never steps it (THE ONE WET CLOCK). Flow runs in real time; the sources and sinks run on the clock's game time —
 * exactly as the film and pond do.
 *
 * NEVER AUTHORITATIVE: nothing here is gameplay. Buoyancy, swimming, footing, vehicles and the server know nothing of
 * this water — it is a picture of rain on the ground.
 *
 * Pure: no three at runtime (the harness bundles it).
 */
import { WET_PARAMS, WET_TIME_SCALE, evaporationMmH, type WetClock } from './wet_state.js';
import { GROUND_LAYER_COUNT, ROUTE_NONE } from './ground_layers.js';
import { activePreset } from './quality_tiers.js';

// ── the patch per tier ──

export interface PuddlePatchConfig { n: number; cell: number }
export type PuddleSimSize = 'off' | 'small' | 'medium' | 'large';
/** Cells per side and the cell (m): 64 / 96 / 128 m windows at 0.5 m (the tiers: med small, high medium, ultra large). */
export const PUDDLE_PATCH: Record<Exclude<PuddleSimSize, 'off'>, PuddlePatchConfig> = {
  small: { n: 128, cell: 0.5 },
  medium: { n: 192, cell: 0.5 },
  large: { n: 256, cell: 0.5 },
};
/** The patch the preset asks for (null = no sim). */
export function puddlePatchFor(preset: { puddleSim?: PuddleSimSize }): PuddlePatchConfig | null {
  const p = preset.puddleSim;
  return p === 'small' || p === 'medium' || p === 'large' ? PUDDLE_PATCH[p] : null;
}
/** The live preset's patch (the ground's build reads it at APPLY time — THE BUILD-TIME LAW). */
export function puddlePatchNow(): PuddlePatchConfig | null { return puddlePatchFor(activePreset() as never); }

// ── THE LOOK (the TSL in puddle_layer.ts mirrors these numbers) ──

/** Water a flat cell RETAINS above its micro-relief before its stagnant sheet drains (m) — the clock's soil film cap. */
export const PUDDLE_RETAIN_FILM = WET_PARAMS.soil.filmCapMm / 1000;
/**
 * The static hollows' depth in the sim's bed at a full `reliefDepression` (m) — CALIBRATED (`verify:puddles` §C, the
 * sweep in the hand-back): the sim's hollows then hold about what the static layer's pond implies, fill in the same
 * couple of minutes of steady rain and dry on the same clock, so the mean puddle cover on a flat plain agrees with the
 * static fill within the harness's band — dry ground and wet ground, light rain and heavy. (A 3 cm hollow looked more
 * "real" and lingered ~2 h: the hand-over edge was a hard line between a drenched patch and a dry plain.)
 */
export const RELIEF_M = 0.006;
/** The ruts' depth at a full rut (m) — a wheel rut holds a little more than a plain's hollow. */
export const RUT_M = 0.012;
/**
 * THE LOOK IS LINEAR IN THE WATER over a fragment's own ground: none at D0 (the film every flat keeps under rain — the
 * retention), a full mirror by D1 = D0 + RELIEF_M. A FULL hollow then reads exactly its depression, which is what the
 * static layer draws at a full pond (flat⁶ × depression × 1) — the two layers agree by construction where the sim has
 * filled what the static layer fills; a terrain low (a bowl, a sump, a rut) is far deeper than RELIEF_M, so its edge
 * stays crisp. (A smoothstep over 0.6–4 mm matched on dry ground only: wet ground's deeper hollows read 50 % more
 * water than the static layer.)
 */
export const PUDDLE_D0 = PUDDLE_RETAIN_FILM;
export const PUDDLE_D1 = PUDDLE_D0 + RELIEF_M;
/**
 * The ground texture's own crevices, in the LOOK only (never the bed — they hold no measurable water): the static
 * depression's `(1 − heightMix) · 0.15` term as a depth, so a fragment in a crevice reads the level a little deeper.
 */
export const LOOK_TEX_RELIEF = 0.15;
export function lookTexReliefM(heightMix: number): number { return RELIEF_M * LOOK_TEX_RELIEF * (1 - heightMix); }
/** The water surface is flat (a pool) under SLOPE0, running (a sheet) past SLOPE1. */
export const PUDDLE_SLOPE0 = 0.03;
export const PUDDLE_SLOPE1 = 0.12;
/** A node counts as wet above this depth (m). */
export const PUDDLE_WET_EPS = 1e-4;
/** A film this deep (m) already reads wet (darkening, gloss) where the clock's own film has dried. */
export const PUDDLE_FILM_M = 0.0015;
/** The hand-over band at the patch's edge (m): the sim → the static fill. */
export const HANDOVER_M = 8;
/** The window recentres (whole cells) when the camera strays this far from its centre (fraction of the side). */
export const RECENTER_FRAC = 0.125;
/** Dagenais' kinematic viscosity for sheet flow on ground (m²/s) and the pipes' damping (1/s). */
export const PUDDLE_VISCOSITY = 1e-5;
export const PUDDLE_DAMPING = 0.5;
/** The exposure (roof map) is re-read into the patch this often (s) and after every recentre. */
export const MASK_REFRESH_S = 1;

// ── THE DECISION ──

export type PuddleReason = 'on' | 'off-tier' | 'webgl2' | 'xr' | 'kill-switch' | 'wet-clock-off' | 'simple-ground';

/**
 * The sim runs when the tier asks for it (`puddleSim`), the backend is WebGPU (compute — read by three's
 * `isWebGPUBackend` flag, THE BACKEND FLAG LAW), no XR session presents, the wet clock runs (it is the sim's only
 * source of rates) and the kill switch is not thrown. Everywhere else the static layer alone draws the puddles.
 */
export function puddleDecision(
  preset: { puddleSim?: string; wetSurfaces?: string; groundQuality?: string },
  env: { webgpu: boolean; xrPresenting: boolean; killSwitch: boolean },
): { on: boolean; reason: PuddleReason } {
  if (env.killSwitch) return { on: false, reason: 'kill-switch' };
  if (!puddlePatchFor(preset as never)) return { on: false, reason: 'off-tier' };
  if ((preset.wetSurfaces ?? 'ground') === 'off') return { on: false, reason: 'wet-clock-off' };
  // coordinator QA (2026-09-30): the SIMPLE ground (minimal / potato's `groundQuality`) has no puddle term — a patch
  // there stepped a compute grid nobody could see (the world leg: 1,693 frames, the ground reading 0 channels)
  if (preset.groundQuality === 'simple') return { on: false, reason: 'simple-ground' };
  if (env.xrPresenting) return { on: false, reason: 'xr' };
  if (!env.webgpu) return { on: false, reason: 'webgl2' };
  return { on: true, reason: 'on' };
}

/** `localStorage 'atlas:puddles' = 'off'` (read safely). */
export function puddleKilled(): boolean {
  try { return globalThis.localStorage?.getItem('atlas:puddles') === 'off'; } catch { return false; }
}

/** The renderer's backend as the module saw it at init (null = not yet known → off). */
let backendWebGPU: boolean | null = null;
export function setPuddleBackend(webgpu: boolean): void { backendWebGPU = webgpu; }
export function puddleBackend(): boolean | null { return backendWebGPU; }

/** The live decision (the ground's build signature and the module read this). */
export function puddleSimDecision(xrPresenting = false): { on: boolean; reason: PuddleReason } {
  return puddleDecision(activePreset() as never, { webgpu: backendWebGPU === true, xrPresenting, killSwitch: puddleKilled() });
}

/** The ground's signature part: '' when off, else the patch's size (a change rebuilds the ground — the buffer is sized). */
export function puddleGroundSig(): string {
  const d = puddleSimDecision();
  if (!d.on) return 'puddle:off';
  const c = puddlePatchFor(activePreset() as never);
  return c ? `puddle:${c.n}` : 'puddle:off';
}

// ── the CPU mirror of three's MaterialX Perlin noise (mx_noise_float over a vec3) — THE STATIC HOLLOWS' OWN NOISE ──

const u32 = (x: number): number => x >>> 0;
const rotl32 = (x: number, k: number): number => u32((x << k) | (x >>> (32 - k)));
function bjfinal(a0: number, b0: number, c0: number): number {
  let a = u32(a0), b = u32(b0), c = u32(c0);
  c = u32(c ^ b); c = u32(c - rotl32(b, 14));
  a = u32(a ^ c); a = u32(a - rotl32(c, 11));
  b = u32(b ^ a); b = u32(b - rotl32(a, 25));
  c = u32(c ^ b); c = u32(c - rotl32(b, 16));
  a = u32(a ^ c); a = u32(a - rotl32(c, 4));
  b = u32(b ^ a); b = u32(b - rotl32(a, 14));
  c = u32(c ^ b); c = u32(c - rotl32(b, 24));
  return c;
}
/** mx_hash_int(x, y, z): Jenkins' lookup3 final mix over three ints. */
function hash3(x: number, y: number, z: number): number {
  const seed = u32(u32(0xdeadbeef) + (3 << 2) + 13);
  return bjfinal(u32(seed + u32(x)), u32(seed + u32(y)), u32(seed + u32(z)));
}
function gradient3(h0: number, x: number, y: number, z: number): number {
  const h = h0 & 15;
  const u = h < 8 ? x : y;
  const v = h < 4 ? y : (h === 12 || h === 14 ? x : z);
  return ((h & 1) ? -u : u) + ((h & 2) ? -v : v);
}
const fade = (t: number): number => t * t * t * (t * (t * 6 - 15) + 10);
/** mx_perlin_noise_float(vec3) — the TSL's arithmetic in doubles (the GPU's is float32; they agree to ~1e-6). */
export function mxPerlin3(px: number, py: number, pz: number): number {
  const X = Math.floor(px), Y = Math.floor(py), Z = Math.floor(pz);
  const fx = px - X, fy = py - Y, fz = pz - Z;
  const u = fade(fx), v = fade(fy), w = fade(fz);
  const g = (dx: number, dy: number, dz: number): number => gradient3(hash3(X + dx, Y + dy, Z + dz), fx - dx, fy - dy, fz - dz);
  const s1 = 1 - u, t1 = 1 - v, r1 = 1 - w;
  const res = r1 * (t1 * (g(0, 0, 0) * s1 + g(1, 0, 0) * u) + v * (g(0, 1, 0) * s1 + g(1, 1, 0) * u))
    + w * (t1 * (g(0, 0, 1) * s1 + g(1, 0, 1) * u) + v * (g(0, 1, 1) * s1 + g(1, 1, 1) * u));
  return 0.982 * res;
}
/** mx_noise_float(vec3(x, y, z), amplitude, pivot). */
export function mxNoiseFloat(x: number, y: number, z: number, amplitude = 1, pivot = 0): number {
  return mxPerlin3(x, y, z) * amplitude + pivot;
}

const smoothstep = (a: number, b: number, x: number): number => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
const clamp01 = (x: number): number => Math.min(1, Math.max(0, x));

// ── THE STATIC HOLLOWS (the CPU mirror of wetness.ts applyGroundV2's puddle term; absolute coordinates) ──

/** The 20 m basins (wetness.ts: smoothstep(0.44, 0.66, mx_noise(pAbs·0.05, 0.5, 0.5))). */
export function staticBasin(ax: number, az: number): number {
  return smoothstep(0.44, 0.66, mxNoiseFloat(ax * 0.05, az * 0.05, 0, 0.5, 0.5));
}
/** The 4 m pockets (wetness.ts: smoothstep(0.52, 0.74, mx_noise(pAbs·0.28 + (11.3, −4.7), 0.5, 0.5))). */
export function staticPocket(ax: number, az: number): number {
  return smoothstep(0.52, 0.74, mxNoiseFloat(ax * 0.28 + 11.3, az * 0.28 - 4.7, 0, 0.5, 0.5));
}
/**
 * How deep a hollow is at a point, 0..1 — the static `depression` WITHOUT its texture term ((1 − heightMix)·0.15, a
 * ground-array height the CPU does not have): clamp((basin·0.6 + pocket·0.5)·(wetMask·0.8 + 0.35), 0, 1).
 */
export function reliefDepression(ax: number, az: number, wetMask: number): number {
  return clamp01((staticBasin(ax, az) * 0.6 + staticPocket(ax, az) * 0.5) * (wetMask * 0.8 + 0.35));
}
/** The wheel ruts on a regional route (the shader's, with the gauge's macro-map jitter at its mean): 0..1. */
export function rutShape(routeD: number, road: number): number {
  const ad = Math.abs(routeD);
  if (!(ad < ROUTE_NONE - 0.1) || !(road > 0.05)) return 0;
  return (1 - smoothstep(0.06, 0.24, Math.abs(ad - 0.78))) * (1 - smoothstep(1.8, 2.6, ad));
}
/** THE MICRO-RELIEF the sim's bed loses at a point (m): the hollows + the ruts. */
export function reliefDepthM(ax: number, az: number, wetMask: number, road: number, routeD: number): number {
  return RELIEF_M * reliefDepression(ax, az, wetMask) + RUT_M * rutShape(routeD, road) * road;
}

// ── infiltration by ground class ──

/**
 * Infiltration relative to the clock's soil pond (loam = 1), per ground layer: sand soaks fast (design rates of the
 * stormwater manuals after Rawls, Brakensiek & Saxton 1982 — sand ~4× loam, gravel ~3×), moss sits on saturated
 * ground, mud and clay hardly drain, rock / strata / snow not at all. Roads are ×(1 − road) (compacted).
 */
export const INFIL_MUL: readonly number[] = [
  /* loam */ 1, /* turf */ 1, /* litter */ 1.5, /* moss */ 0.5, /* rock */ 0.03, /* strata */ 0.03,
  /* gravel */ 3, /* beach */ 4, /* dune */ 4, /* mud */ 0.2, /* snow */ 0, /* alien */ 1,
];
export function infiltrationMul(weights12: ArrayLike<number>, road: number): number {
  let m = 0, s = 0;
  for (let k = 0; k < GROUND_LAYER_COUNT; k++) { const w = Math.max(0, weights12[k] ?? 0); m += w * INFIL_MUL[k]; s += w; }
  return (s > 1e-6 ? m / s : 1) * clamp01(1 - road);
}

// ── the rates, READ from the one wet clock (m per REAL second) ──

export interface PuddleRates { rainMs: number; evapMs: number; infilMs: number; timeScale: number; rainMmH: number; evapMmH: number; infilMmH: number }
/** The clock's rain / evaporation / the soil pond's drain now (never steps the clock). */
export function ratesFromClock(clock: Pick<WetClock, 'lastInputs' | 'state' | 'timeScale'>, timeScaleOverride?: number): PuddleRates {
  const ts = timeScaleOverride ?? clock.timeScale ?? WET_TIME_SCALE;
  const inp = clock.lastInputs;
  if (!inp) return { rainMs: 0, evapMs: 0, infilMs: 0, timeScale: ts, rainMmH: 0, evapMmH: 0, infilMmH: 0 };
  const P = WET_PARAMS.soil;
  const rainMmH = Math.max(0, inp.rainMmH);
  const evapMmH = Math.max(0, evaporationMmH(inp.tempC, inp.rh, inp.windMs, inp.sun, P.sunGainC, inp.cool ?? 0));
  const poreRoom = P.poreCapMm > 0 ? Math.max(0, 1 - clock.state.soil.pores / P.poreCapMm) : 0;
  const infilMmH = (P.infilMmH / 3) * Math.max(poreRoom, 0.25); // the static pond's own drain rule
  const k = ts / 3.6e6; // mm per game hour → m per real second
  return { rainMs: rainMmH * k, evapMs: evapMmH * k, infilMs: infilMmH * k, timeScale: ts, rainMmH, evapMmH, infilMmH };
}

// ── THE WINDOW (whole cells on one world lattice, so a bed sample never moves) ──

export class PuddleWindow {
  /** Absolute position of node (0, 0). */
  minX = 0;
  minZ = 0;
  placed = false;
  constructor(readonly n: number, readonly cell: number) {}
  get side(): number { return (this.n - 1) * this.cell; }
  get centerX(): number { return this.minX + this.side / 2; }
  get centerZ(): number { return this.minZ + this.side / 2; }
  nodeX(col: number): number { return this.minX + col * this.cell; }
  nodeZ(row: number): number { return this.minZ + row * this.cell; }
  /** Where the window wants its min corner for a camera at (x, z) — snapped to the world lattice. */
  snappedMin(x: number, z: number): [number, number] {
    const c = this.cell, h = this.side / 2;
    return [Math.round((x - h) / c) * c, Math.round((z - h) / c) * c];
  }
  /**
   * Follow the camera: the first call places the window; later calls move it by whole cells once the camera strays
   * RECENTER_FRAC of a side from the centre. Returns the shift (dc, dr) in cells (0, 0 = none), or 'placed'.
   */
  follow(x: number, z: number): { dc: number; dr: number } | 'placed' {
    const [mx, mz] = this.snappedMin(x, z);
    if (!this.placed) { this.minX = mx; this.minZ = mz; this.placed = true; return 'placed'; }
    const far = RECENTER_FRAC * this.side;
    if (Math.abs(x - this.centerX) < far && Math.abs(z - this.centerZ) < far) return { dc: 0, dr: 0 };
    const dc = Math.round((mx - this.minX) / this.cell), dr = Math.round((mz - this.minZ) / this.cell);
    this.minX += dc * this.cell; this.minZ += dr * this.cell;
    return { dc, dr };
  }
  /** The hand-over weight at an absolute point: 1 inside, 0 at the edge, smoothstep over HANDOVER_M. */
  weightAt(x: number, z: number): number {
    const d = Math.min(x - this.minX, this.minX + this.side - x, z - this.minZ, this.minZ + this.side - z);
    return smoothstep(0, HANDOVER_M, d - this.cell);
  }
}

// ── THE BED: one cell from the drawn ground ──

export interface PuddleGround {
  /** The DRAWN ground height at an absolute point (the resident chunk's triangles — `meshHeight`). */
  height(x: number, z: number): number;
  /** The ground rules at the point (interpolated like the shader's varyings), or null when no chunk is resident. */
  rules(x: number, z: number): { weights: ArrayLike<number>; road: number; wet: number; routeD: number } | null;
  /** The still water level over the point (sea, lake, river) or null. */
  still(x: number, z: number): number | null;
  /** A building stands here at this ground height. */
  solid(x: number, z: number, groundY: number): boolean;
}

export interface PuddleCell { bed: number; infilMul: number; solid: boolean; drain: boolean; relief: number; ground: number; retain: number }
export function puddleCell(g: PuddleGround, x: number, z: number): PuddleCell {
  const ground = g.height(x, z);
  const r = g.rules(x, z);
  const relief = r ? reliefDepthM(x, z, r.wet, r.road, r.routeD) : RELIEF_M * reliefDepression(x, z, 0);
  const still = g.still(x, z);
  const drain = still !== null && still >= ground - relief - 0.02;
  return { bed: ground - relief, ground, relief, retain: relief + PUDDLE_RETAIN_FILM, infilMul: r ? infiltrationMul(r.weights, r.road) : 1, solid: g.solid(x, z, ground), drain };
}

/** The engine's per-cell source layout (`ShallowWaterGpu.src`): infiltration multiplier, exposure (kept), kind (0 ground | 1 solid | 2 drain), retention. */
export function packCell(src: Float32Array, i: number, c: PuddleCell): void {
  src[i * 4] = c.infilMul;
  src[i * 4 + 2] = c.solid ? 1 : c.drain ? 2 : 0;
  src[i * 4 + 3] = c.retain;
}

// ── THE LOOK's CPU mirrors (the calibration harness and the bed judge against these) ──

/** The sim's mirror at a fragment: water `depth` (m) over the fragment's own ground, under a water surface of `slope`. */
export function simCoverage(depth: number, slope: number): number {
  return clamp01((depth - PUDDLE_D0) / (PUDDLE_D1 - PUDDLE_D0)) * (1 - smoothstep(PUDDLE_SLOPE0, PUDDLE_SLOPE1, slope));
}
/**
 * THE GROUND'S READ OF THE SIM, operation for operation (`lib/puddle_layer.ts puddleGroundTerm`, with `uOn` = 1): the
 * patch's look buffer (vec4 a node: depth, water level rel. `refY`, the water surface's slope, valid), node (0, 0) at
 * absolute (minX, minZ); a fragment at absolute (x, y, z) whose micro-relief there is `reliefM` (the bed's + the texture's
 * crevices, `lookTexReliefM`). The level is interpolated
 * over the WET nodes only and compared with the fragment's own ground (y − reliefM), so a puddle's edge follows the
 * fragment's ground, not the 0.5 m lattice. The harness and the bed judge the GPU against this.
 */
export function puddleLookCpu(
  look: ArrayLike<number>, n: number, cell: number, minX: number, minZ: number, refY: number,
  x: number, y: number, z: number, reliefM: number,
): { cover: number; wet: number; weight: number; depth: number } {
  const lx = (x - minX) / cell, lz = (z - minZ) / cell;
  const c0 = Math.min(n - 2, Math.max(0, Math.floor(lx))), r0 = Math.min(n - 2, Math.max(0, Math.floor(lz)));
  const fx = clamp01(lx - c0), fz = clamp01(lz - r0);
  const i00 = r0 * n + c0, idx = [i00, i00 + 1, i00 + n, i00 + n + 1];
  const w = [(1 - fx) * (1 - fz), fx * (1 - fz), (1 - fx) * fz, fx * fz];
  let ws = 0, etaS = 0, slope = 0, valid = 0;
  for (let k = 0; k < 4; k++) {
    const j = idx[k] * 4, m = look[j] > PUDDLE_WET_EPS ? w[k] : 0;
    ws += m; etaS += look[j + 1] * m; slope += look[j + 2] * w[k]; valid += look[j + 3] * w[k];
  }
  const eta = etaS / Math.max(ws, 1e-6) + refY;
  const depth = eta - (y - reliefM);
  const present = smoothstep(0, 0.25, ws);
  const cover = clamp01((depth - PUDDLE_D0) / (PUDDLE_D1 - PUDDLE_D0)) * (1 - smoothstep(PUDDLE_SLOPE0, PUDDLE_SLOPE1, slope)) * present;
  const wet = smoothstep(0, PUDDLE_FILM_M, depth) * present;
  const edge = Math.min(Math.min(lx, n - 1 - lx), Math.min(lz, n - 1 - lz)) * cell;
  const weight = smoothstep(0, HANDOVER_M, edge - cell) * valid;
  return { cover, wet, weight, depth: ws > 0 ? depth : -1 };
}

/**
 * The patch's water-surface slope at a node — the kernels' rule (max |central difference| of the level over the wet
 * neighbours; a dry or solid neighbour, or the window's edge, reads the node's own level).
 */
export function nodeSlope(bed: ArrayLike<number>, depth: ArrayLike<number>, solid: ArrayLike<number> | null, n: number, cell: number, i: number): number {
  const col = i % n, row = (i - col) / n, head = bed[i] + depth[i];
  const etaOf = (okN: boolean, j: number): number => (okN && depth[j] > PUDDLE_WET_EPS && !(solid && solid[j]) ? bed[j] + depth[j] : head);
  const gx = (etaOf(col < n - 1, i + 1) - etaOf(col > 0, i - 1)) / (2 * cell);
  const gz = (etaOf(row < n - 1, i + n) - etaOf(row > 0, i - n)) / (2 * cell);
  return Math.max(Math.abs(gx), Math.abs(gz));
}

/** The static layer's cover (wetness.ts): flat^6 × depression × the fill ramp of the clock's pond. */
export function staticCoverage(depression: number, flatPow6: number, pond01: number): number {
  return flatPow6 * depression * staticFill(pond01);
}
/** THE STATIC FILL RAMP (wetness.ts `smoothstep(PUDDLE_FILL_LO, PUDDLE_FILL_HI, pondLocal)`), calibrated against the sim. */
export const PUDDLE_FILL_LO = 0.25;
export const PUDDLE_FILL_HI = 0.7;
export function staticFill(pond01: number): number { return smoothstep(PUDDLE_FILL_LO, PUDDLE_FILL_HI, pond01); }

// ── diagnostics shared with the module / bed ──

export interface PuddleStats {
  on: boolean; reason: PuddleReason; n: number; cell: number;
  minX: number; minZ: number; shifts: number; substeps: number; dt: number; lastSubsteps: number;
  rates: PuddleRates | null; volume: number; wetCells: number; maxDepth: number;
  ledger: Record<string, number> | null; budgetResidual: number | null; bedBuildMs: number; bedCells: number;
}
